/**
 * The console's ONE calendar — a month grid, in the shape shadcn/ui's Calendar
 * draws (user, 2026-09-10: "use https://ui.shadcn.com/docs/components/base/calendar").
 *
 * ## Why this exists rather than the browser's
 *
 * The due-date field used to open the platform picker. That was a deliberate
 * choice and the wrong one: Chrome's dialog is its own grey-and-blue world with
 * its own type, and dropped on a warm console it read as another application's
 * window. Keeping the native control also meant the field could never match the
 * assignee pill beside it, because half its chrome belonged to the browser.
 *
 * What the native picker was buying — timezone, locale and keyboard correctness
 * — is bought here instead by never touching either: every date in and out is a
 * `YYYY-MM-DD` string, built from LOCAL calendar parts, and the grid is a plain
 * list of buttons so the browser's own focus order and Enter/Space work without
 * a roving-tabindex implementation to get wrong.
 *
 * ## Why it is shared
 *
 * Two callers draw a month: the review field table and the item's properties
 * column (design 13d). They had two grids with two sets of numbers, which is
 * the same mistake `TABLE_TYPE` was extracted to fix one screen earlier. One
 * calendar, two callers.
 *
 * ## shadcn's anatomy, on this console's palette
 *
 * A shadcn calendar dropped in verbatim would be as foreign as Chrome's: it is
 * a neutral grey component and this console is warm paper and ink. So the
 * GEOMETRY is shadcn's — 32px cells, a 12px gutter, a centred caption between
 * two square nav buttons, six rows always — and the colour is `tokens.ts`.
 */

import { useMemo, useState } from "react";
import { C, FONT_MONO } from "../tokens";
import { MONTHS, WEEKDAYS_SHORT } from "./chrome";

/** `YYYY-MM-DD` in the READER's zone. Built from the parts, never
 *  `toISOString`, which is the UTC day and is tomorrow's date for an evening
 *  west of Greenwich. */
export function isoDay(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Local midnight of a `YYYY-MM-DD`, or `null`. Parsed from the parts for the
 *  same reason. */
export function parseDay(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function shift(from: Date, days: number): Date {
  return new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
}

/** Full month names — the caption reads `September 2026`, where `MONTHS` are
 *  the three-letter forms a date chip wants. */
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * The six weeks a month grid draws, Sunday-first.
 *
 * Always six rows, even when five would hold the month: a grid that changed
 * height as you paged would move the footer — and the pointer heading for it —
 * under the reader's hand.
 */
export function monthGrid(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = shift(first, -first.getDay());
  return Array.from({ length: 42 }, (_, index) => shift(start, index));
}

/** A closed range of local days, inclusive at BOTH ends — `YYYY-MM-DD`. */
export interface DayRange {
  from: string;
  to: string;
}

export interface CalendarProps {
  /** Single-date mode: `YYYY-MM-DD`, or empty for no selection. */
  value?: string;
  /** Single-date mode. `""` when the footer's Clear is pressed. */
  onSelect?: (next: string) => void;
  /**
   * Range mode: the band to paint, or `null` for none.
   *
   * Passing this at all (even `null`) puts the grid in range mode, and the
   * caller owns the two-click state: `onDayClick` reports raw clicks and the
   * caller decides what a click means — first end, second end, or a redo.
   *
   * That split is deliberate. shadcn's calendar owns range state internally,
   * which is fine when the panel is only a calendar; the photo filter's panel
   * also has presets that write a range, a footer that has to say "Pick the
   * other day" mid-selection, and a rule that a backwards pair is sorted
   * rather than refused. All three need the half-made range, so it lives
   * where they are. The grid just paints the band it is given.
   *
   * A single date is a band whose ends are the same day, which is how one set
   * of cell styles serves both modes.
   */
  selection?: DayRange | null;
  /** Range mode: one day was clicked. */
  onDayClick?: (day: string) => void;
  /** Range mode: the hovered day, `null` on leave — so a caller can preview
   *  the band a second click would make. */
  onDayHover?: (day: string | null) => void;
  /** Today, as the caller measures it — so a fixed clock in a test reaches the
   *  grid rather than the grid reading its own. */
  today: Date;
  /** Hide the Clear/Today footer where the caller offers its own way out. */
  footer?: boolean;
  /**
   * A dot under a day — its colour, or nothing.
   *
   * The daily log's "Jump to date" marks every day that logged something, so
   * a reader paging back through September can see WHERE the walks are
   * instead of clicking empty days to find out. The due-date callers pass
   * nothing and get the plain grid they had.
   *
   * Optional rather than a `days` prop on purpose: a calendar that knew what
   * a "walk" was could not be the calendar a due-date field uses.
   */
  markerFor?: (day: string) => string | undefined;
  /**
   * Days after this `YYYY-MM-DD` cannot be picked.
   *
   * A daily log has no tomorrow, so an offered tomorrow can only ever land on
   * an empty report. A due date has the opposite need — the future is the
   * whole point — which is why this is the caller's to set.
   */
  maxDay?: string;
  /**
   * Forward paging stops once the month holding this day is on screen.
   *
   * Without it `maxDay` produces a dead end rather than a boundary: the arrow
   * still works, and it pages into a month where every cell is disabled,
   * which reads as the calendar having broken. The design dims the arrow
   * instead ("Led AI Console.dc.html", `calNextOp`).
   */
  maxMonth?: string;
  testId?: string;
}

export function Calendar({
  value = "",
  onSelect,
  selection,
  onDayClick,
  onDayHover,
  today,
  footer = true,
  markerFor,
  maxDay,
  maxMonth,
  testId = "calendar",
}: CalendarProps) {
  // One selection model: a band. Single mode is a band of one day, so the
  // cell styling below has no second branch to drift out of step.
  const band: DayRange | null =
    selection !== undefined ? selection : value ? { from: value, to: value } : null;
  const selected = parseDay(band?.to ?? "");
  const [month, setMonth] = useState(() => {
    const anchor = selected ?? today;
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  });

  const days = useMemo(() => monthGrid(month), [month]);
  // Compared as `YYYY-MM`, so the check is a string compare rather than two
  // getters that have to agree about year rollover.
  const atMaxMonth =
    maxMonth !== undefined && isoDay(month).slice(0, 7) >= maxMonth.slice(0, 7);
  const todayKey = isoDay(today);

  return (
    <div
      data-testid={testId}
      style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}
    >
      {/* Caption between two square nav buttons — shadcn's header, which puts
          the month where the eye already is rather than off to one side. */}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <MonthStep
          direction="prev"
          testId={`${testId}-prev`}
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
        />
        <span
          data-testid={`${testId}-caption`}
          style={{
            flex: 1,
            textAlign: "center",
            fontSize: 15.52,
            fontWeight: 600,
            letterSpacing: "-0.1px",
          }}
        >
          {`${MONTH_NAMES[month.getMonth()]} ${month.getFullYear()}`}
        </span>
        <MonthStep
          direction="next"
          testId={`${testId}-next`}
          disabled={atMaxMonth}
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
        />
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(7, 32px)", gap: 2 }}
        onMouseLeave={() => onDayHover?.(null)}
      >
        {WEEKDAYS_SHORT.map((day, index) => (
          <span
            key={day}
            aria-hidden
            style={{
              width: 32,
              height: 26,
              display: "grid",
              placeItems: "center",
              fontSize: 13.23,
              color: C.propLabel,
            }}
          >
            {/* Two letters, not one: `S` twice and `T` twice is a header a
                reader has to count along rather than read. */}
            {day.slice(0, 2)}
            <span style={{ display: "none" }}>{index}</span>
          </span>
        ))}

        {days.map((date) => {
          const key = isoDay(date);
          const outside = date.getMonth() !== month.getMonth();
          const isStart = band !== null && key === band.from;
          const isEnd = band !== null && key === band.to;
          const inside = band !== null && key > band.from && key < band.to;
          // "Selected" now means an END of the band. A single date is both.
          const isSelected = isStart || isEnd;
          const isToday = key === todayKey;
          const blocked = maxDay !== undefined && key > maxDay;
          const marker = markerFor?.(key);
          return (
            <button
              key={key}
              type="button"
              className="kb"
              data-testid={`${testId}-day`}
              data-day={key}
              data-selected={isSelected ? "true" : undefined}
              data-inside={inside ? "true" : undefined}
              data-today={isToday ? "true" : undefined}
              data-marked={marker ? "true" : undefined}
              aria-current={isToday ? "date" : undefined}
              aria-pressed={isSelected}
              disabled={blocked}
              onMouseEnter={() => onDayHover?.(key)}
              onClick={() => (onDayClick ? onDayClick(key) : onSelect?.(key))}
              style={{
                width: 32,
                height: 32,
                // A marked grid stacks the dot under the number; an unmarked
                // one keeps the single centred cell the due-date fields have.
                display: markerFor ? "flex" : "grid",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: markerFor ? 2 : 0,
                placeItems: "center",
                // Square on the inner side so a run of days reads as one
                // band rather than a row of separate pills. A single date is
                // both ends at once and stays fully rounded.
                borderRadius:
                  isStart && isEnd
                    ? 8
                    : isStart
                      ? "8px 2px 2px 8px"
                      : isEnd
                        ? "2px 8px 8px 2px"
                        : inside
                          ? 2
                          : 8,
                border: "none",
                padding: 0,
                font: "inherit",
                fontSize: 14.95,
                // Today is WEIGHT, not another fill: a filled today beside a
                // filled selection is two claims of "this one" on one grid.
                fontWeight: isSelected || isToday ? 700 : 500,
                cursor: blocked ? "default" : "pointer",
                background: isSelected
                  ? C.ink
                  : inside
                    ? C.weekSelected
                    : isToday
                      ? C.faint
                      : "transparent",
                color: isSelected
                  ? C.surface
                  : blocked || outside
                    ? C.calOutside
                    : C.ink,
              }}
            >
              <span style={{ lineHeight: 1 }}>{date.getDate()}</span>
              {/* The dot's ROW is reserved whether or not this day has one, so
                  paging from a busy month to a quiet one does not shift every
                  number up three pixels. */}
              {markerFor && (
                <span
                  aria-hidden
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: 2,
                    // On the selected day's ink fill a coloured dot goes
                    // muddy; it becomes the paper colour and still reads.
                    background: !marker ? "transparent" : isSelected ? C.surface : marker,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      {footer && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: `1px solid ${C.railEdge}`,
            paddingTop: 8,
            marginTop: 2,
          }}
        >
          <FooterAction
            testId={`${testId}-clear`}
            onClick={() => onSelect?.("")}
            disabled={!value}
          >
            Clear
          </FooterAction>
          <FooterAction
            testId={`${testId}-today`}
            onClick={() => (onDayClick ? onDayClick(todayKey) : onSelect?.(todayKey))}
          >
            Today
          </FooterAction>
        </div>
      )}
    </div>
  );
}

function MonthStep({
  direction,
  testId,
  disabled,
  onClick,
}: {
  direction: "prev" | "next";
  testId: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="kb"
      data-testid={testId}
      aria-label={direction === "prev" ? "Previous month" : "Next month"}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 28,
        height: 28,
        flex: "none",
        display: "grid",
        placeItems: "center",
        borderRadius: 8,
        border: `1px solid ${C.hair}`,
        background: C.surface,
        // Dimmed rather than hidden: an arrow that vanished at the boundary
        // would move the caption under the cursor.
        color: disabled ? C.calOutside : C.sub,
        cursor: disabled ? "default" : "pointer",
        padding: 0,
      }}
    >
      <svg width="7" height="11" viewBox="0 0 8 12" fill="none" aria-hidden>
        <path
          d={direction === "prev" ? "M6.5 1L1.5 6l5 5" : "M1.5 1l5 5-5 5"}
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function FooterAction({
  children,
  testId,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  testId: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="kb"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      style={{
        border: "none",
        background: "transparent",
        padding: "0 2px",
        font: "inherit",
        fontFamily: FONT_MONO,
        fontSize: 12.08,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: disabled ? C.calOutside : C.goldInk,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {children}
    </button>
  );
}
